/**
 * Founding-member capture, shown wherever a paid CTA would open checkout while
 * billing is not live. Honest offer: the launch price locked in + first access
 * when checkout opens. Email is validated in the browser AND on the server
 * (`waitlist.join`), with a honeypot and a stored consent record.
 */
import { useMutation } from 'convex/react'
import { ArrowRight, Check, Sparkles } from 'lucide-react'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { api } from '../../../convex/_generated/api'
import { trackEvent } from '../../lib/analytics'
import type { ConversionSurface } from '../../lib/analytics'
import { anonIdCookieName } from '../../lib/anonymous-state'
import { getCookie } from '../../lib/cookies'
import { isValidEmail } from '../../lib/email'
import { FOUNDING_CONSENT_VERSION } from '../../lib/email-consent'
import { localizedPath, useTranslation } from '../../lib/i18n'
import type { BillingInterval, PaidPlanId } from '../../lib/pricing'

/** Stored with every row; bump in src/lib/email-consent.ts on copy changes. */
export const WAITLIST_CONSENT_VERSION = FOUNDING_CONSENT_VERSION

/** Anchor every waitlist CTA scrolls to. */
export const FOUNDING_ANCHOR_ID = 'founding'

type Status =
  | 'idle'
  | 'saving'
  | 'pending'
  | 'joined'
  | 'updated'
  | 'invalid_email'
  | 'consent_required'
  | 'rate_limited'
  | 'error'

export function FoundingWaitlist({
  plan,
  onPlanChange,
  interval,
  surface,
}: {
  plan: PaidPlanId
  onPlanChange: (plan: PaidPlanId) => void
  interval: BillingInterval
  surface: ConversionSurface
}) {
  const { locale, t, dict } = useTranslation()
  const join = useMutation(api.waitlist.join)
  const [status, setStatus] = useState<Status>('idle')

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    const email = String(data.get('email') ?? '')
    const consent = data.get('consent') === 'on'
    if (!isValidEmail(email)) {
      setStatus('invalid_email')
      return
    }
    if (!consent) {
      setStatus('consent_required')
      return
    }
    setStatus('saving')
    try {
      const result = await join({
        email,
        plan,
        interval,
        locale,
        source: surface,
        consent,
        consentTextVersion: WAITLIST_CONSENT_VERSION,
        anonId: getCookie(anonIdCookieName) ?? undefined,
        website: String(data.get('website') ?? '') || undefined,
      })
      setStatus(result.status)
      if (
        result.status === 'pending' ||
        result.status === 'joined' ||
        result.status === 'updated'
      ) {
        trackEvent('waitlist_joined', {
          surface,
          plan,
          interval,
          status: result.status,
        })
        form.reset()
      }
    } catch {
      setStatus('error')
    }
  }

  const done =
    status === 'pending' || status === 'joined' || status === 'updated'
  const message: Record<Exclude<Status, 'idle' | 'saving'>, string> = {
    pending: t('pricing.founding.pending'),
    joined: t('pricing.founding.success'),
    updated: t('pricing.founding.updated'),
    invalid_email: t('pricing.founding.invalidEmail'),
    consent_required: t('pricing.founding.consentRequired'),
    rate_limited: t('pricing.founding.rateLimited'),
    error: t('forms.genericError'),
  }

  return (
    <section
      className="founding-panel"
      id={FOUNDING_ANCHOR_ID}
      aria-labelledby="founding-title"
      tabIndex={-1}
    >
      <div className="founding-copy">
        <span className="eyebrow founding-eyebrow">
          <Sparkles size={12} aria-hidden="true" />
          {t('pricing.founding.eyebrow')}
        </span>
        <h2 id="founding-title">{t('pricing.founding.title')}</h2>
        <p>{t('pricing.founding.text')}</p>
        <ul className="founding-perks">
          {dict.pricing.founding.perks.map((perk) => (
            <li key={perk}>
              <span className="founding-perk-icon" aria-hidden="true">
                <Check size={13} strokeWidth={3} />
              </span>
              {perk}
            </li>
          ))}
        </ul>
      </div>

      <form className="founding-form" onSubmit={onSubmit} noValidate>
        <fieldset className="founding-plan-picker">
          <legend>{t('pricing.founding.planLabel')}</legend>
          {(['pro', 'team'] as const).map((option) => (
            <label
              key={option}
              className={
                plan === option ? 'founding-plan is-active' : 'founding-plan'
              }
            >
              <input
                type="radio"
                name="plan"
                value={option}
                checked={plan === option}
                onChange={() => onPlanChange(option)}
              />
              <span>{dict.pricing.plans[option].name}</span>
            </label>
          ))}
          <span className="founding-interval">
            {interval === 'year'
              ? t('pricing.founding.intervalYearly')
              : t('pricing.founding.intervalMonthly')}
          </span>
        </fieldset>

        <div className="founding-field">
          <label htmlFor={`founding-email-${surface}`}>
            {t('pricing.founding.emailLabel')}
          </label>
          <input
            id={`founding-email-${surface}`}
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t('pricing.founding.emailPlaceholder')}
            aria-invalid={status === 'invalid_email' || undefined}
            required
          />
        </div>

        <input
          className="hp-field"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        <label className="founding-consent">
          <input type="checkbox" name="consent" required />
          <span className="founding-check" aria-hidden="true">
            <Check size={13} strokeWidth={3} />
          </span>
          <span>
            {t('pricing.founding.consent')}{' '}
            <a href={localizedPath('privacy', locale)}>
              {t('pricing.founding.consentLink')}
            </a>
            .
          </span>
        </label>

        <button
          className="primary-button founding-submit"
          type="submit"
          disabled={status === 'saving'}
        >
          {status === 'saving'
            ? t('pricing.founding.submitting')
            : t('pricing.founding.submit')}
          <ArrowRight size={17} aria-hidden="true" />
        </button>

        {status !== 'idle' && status !== 'saving' ? (
          <p
            className={done ? 'form-status is-success' : 'form-status is-error'}
            role="status"
          >
            {message[status]}
          </p>
        ) : null}
        <small className="founding-fineprint">
          {t('pricing.founding.fineprint')}
        </small>
      </form>
    </section>
  )
}

